import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import HeaderFirst from 'utils/HeaderFirst';
import HeaderButton from 'utils/headerButton';
import CustomBootstrapTable from 'utils/reactTable';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import TableLoader from 'components/Loader/TableLoader';
import { formatMontant } from 'utils/numberToWordsFr';
import { useGetFactureByIdQuery, useGetFacturesQuery } from 'actions/facturation/queries';
import {
  useAnnulerFactureMutation,
  useConvertirDevisMutation,
  useDeleteFactureMutation,
  useEmettreFactureMutation,
  useRelancerFactureMutation,
} from 'actions/facturation/mutations';
import '../ventes/client.css';
import TablePaginators from '../ventes/TablePaginator';
import FactureForm from './FactureForm';
import FacturePreview from './FacturePreview';
import PayFactureModal from './PayFactureModal';
import ModalGenererAvoir from './ModalGenererAvoir';
import './facturation.css';

// Devis actives (livraison 2)
const AFFICHER_DEVIS = true;

const statutStyle = (s) => ({
  'Brouillon': { color: '#374151', bg: '#f3f4f6' },
  'Envoyé': { color: '#1e40af', bg: '#dbeafe' },
  'Accepté': { color: '#065f46', bg: '#d1fae5' },
  'Émis': { color: '#1e40af', bg: '#dbeafe' },
  'En attente': { color: '#92400e', bg: '#fef3c7' },
  'Payé': { color: '#065f46', bg: '#d1fae5' },
  'En retard': { color: '#991b1b', bg: '#fee2e2' },
  'Annulé': { color: '#6b7280', bg: '#e5e7eb' },
}[s] || { color: '#374151', bg: '#f3f4f6' });

const StatutPill = ({ value }) => {
  const s = statutStyle(value);
  return (
    <span
      style={{
        color: s.color,
        backgroundColor: s.bg,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        display: 'inline-block',
      }}
    >
      {value}
    </span>
  );
};

export default function FacturationPage() {
  const { clientID: dossier_id } = useParams();
  const [mode, setMode] = useState('list'); // list | create | edit | preview
  const [createType, setCreateType] = useState('facture');
  const [previewId, setPreviewId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [avoirTarget, setAvoirTarget] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [serachTerm, setSearchedTerm] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [paginationIndex, setPaginationIndex] = useState(0);

  const { data, isFetching } = useGetFacturesQuery({ dossier_id });
  const { data: editFacture } = useGetFactureByIdQuery(
    { id: editId, dossier_id },
    { skip: !editId }
  );
  const [emettreFacture] = useEmettreFactureMutation();
  const [convertirDevis] = useConvertirDevisMutation();
  const [annulerFacture] = useAnnulerFactureMutation();
  const [relancerFacture] = useRelancerFactureMutation();
  const [deleteFacture, { isLoading: isDeleting }] = useDeleteFactureMutation();

  const donnees = data?.data || [];

  const errMsg = (e) =>
    e?.data?.message && typeof e.data.message === 'string'
      ? e.data.message
      : 'Une erreur est survenue';

  const handleRowSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedRows((prev) => [...prev, row.id]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleSearchChange = (v) => {
    setSearchedTerm(v);
    setPaginationIndex(0);
  };

  const visibles = useMemo(
    () => donnees.filter((f) => AFFICHER_DEVIS || f.type !== 'devis'),
    [donnees]
  );

  const filtrees = useMemo(
    () =>
      visibles.filter((f) => {
        if (filtreType && f.type !== filtreType) return false;
        if (filtreStatut === 'Impayées') {
          if (!['En attente', 'En retard'].includes(f.statut)) return false;
        } else if (filtreStatut && f.statut !== filtreStatut) return false;
        if (serachTerm) {
          const q = serachTerm.toLowerCase();
          const qNum = q.replace(/[\s\u00a0]/g, '');
          return (
            (f.numero || '').toLowerCase().includes(q) ||
            (f.client_nom_complet || '').toLowerCase().includes(q) ||
            (f.client_ninea_complet || '').toLowerCase().includes(q) ||
            (qNum && String(f.net_a_payer || '').includes(qNum))
          );
        }
        return true;
      }),
    [visibles, filtreType, filtreStatut, serachTerm]
  );

  const currentPageData = filtrees.slice(
    paginationIndex * pageSize,
    (paginationIndex + 1) * pageSize
  );

  const kpis = useMemo(() => {
    const facs = visibles.filter((f) => f.type === 'facture' && f.statut !== 'Annulé');
    const totalFacture = facs.reduce((s, f) => s + (f.total_ttc || 0), 0);
    const encaisse = facs.reduce((s, f) => s + (f.montant_paye || 0), 0);
    const devis = visibles.filter((f) => f.type === 'devis');
    const devisActifs = devis.filter((f) => !['Annulé', 'Accepté'].includes(f.statut));
    return {
      totalFacture,
      encaisse,
      recouvrement:
        totalFacture > 0 ? Math.round((encaisse / totalFacture) * 100) : 0,
      enAttente: facs
        .filter((f) => ['En attente', 'En retard'].includes(f.statut))
        .reduce((s, f) => s + (f.net_a_payer || 0) - (f.montant_paye || 0), 0),
      nbDevis: devisActifs.length,
      montantDevis: devisActifs.reduce((s, f) => s + (f.net_a_payer || 0), 0),
      tauxConversion: devis.length
        ? Math.round(
            (devis.filter((f) => f.statut === 'Accepté').length / devis.length) * 100
          )
        : 0,
    };
  }, [visibles]);

  // ----- actions sur une ligne -----
  const onEmettre = async (f) => {
    try {
      await emettreFacture({ id: f.id }).unwrap();
      toast.success(`Facture ${f.numero} émise — créance comptable créée`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onConvertir = async (f) => {
    try {
      const res = await convertirDevis({ id: f.id }).unwrap();
      toast.success(`Devis converti en facture ${res.numero}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onAnnuler = async (f) => {
    try {
      await annulerFacture({ id: f.id }).unwrap();
      toast.success(`${f.numero} annulé`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onAvoir = (f) => {
    setAvoirTarget(f);
  };

  const onRelancer = async (f) => {
    try {
      await relancerFacture({ id: f.id }).unwrap();
      toast.success(`Relance envoyée au client pour ${f.numero}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // ----- actions barre d'outils (selection) -----
  const selectedDocs = donnees.filter((f) => selectedRows.includes(f.id));

  const handleEyesClick = () => {
    if (selectedRows.length === 1) {
      setPreviewId(selectedRows[0]);
      setMode('preview');
    }
  };

  const onClickDelete = () => {
    if (!selectedRows.length) {
      toast.warning('Sélectionnez au moins un document.');
      return;
    }
    if (selectedDocs.some((f) => f.statut !== 'Brouillon')) {
      toast.warning('Seuls les brouillons peuvent être supprimés — utilisez "Annuler" pour les autres.');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const results = await Promise.all(
        selectedRows.map((id) =>
          deleteFacture({ id }).unwrap().catch((error) => ({ error, id }))
        )
      );
      const errors = results.filter((r) => r && r.error);
      const ok = selectedRows.length - errors.length;
      if (ok > 0) toast.success(`${ok} brouillon(s) supprimé(s).`);
      errors.forEach(({ error }) => toast.error(errMsg(error)));
      setSelectedRows([]);
      setShowDeleteModal(false);
    } catch (e) {
      toast.error(errMsg(e));
      setShowDeleteModal(false);
    }
  };

  // ----- colonnes (meme style que les autres pages) -----
  const columns = [
    {
      dataField: 'numero',
      text: 'N°',
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          <b>{cell}</b>
        </div>
      ),
    },
    {
      dataField: 'type',
      text: 'Type',
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          {cell === 'devis' ? 'Devis' : cell === 'avoir' ? 'Avoir' : 'Facture'}
        </div>
      ),
    },
    {
      dataField: 'client_nom_complet',
      text: 'Client',
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          {cell}
        </div>
      ),
    },
    {
      dataField: 'date_emission',
      text: 'Date',
      formatter: (cell, row) => {
        const d = new Date(cell);
        const fmt = `${String(d.getDate()).padStart(2, '0')}/${String(
          d.getMonth() + 1
        ).padStart(2, '0')}/${d.getFullYear()}`;
        return <div onClick={() => handleRowSelect(row)}>{fmt}</div>;
      },
    },
    {
      dataField: 'date_echeance',
      text: 'Échéance',
      formatter: (cell, row) => {
        if (!cell) return <div onClick={() => handleRowSelect(row)}>—</div>;
        const d = new Date(cell);
        const fmt = `${String(d.getDate()).padStart(2, '0')}/${String(
          d.getMonth() + 1
        ).padStart(2, '0')}/${d.getFullYear()}`;
        return <div onClick={() => handleRowSelect(row)}>{fmt}</div>;
      },
    },
    {
      dataField: 'net_a_payer',
      text: 'Net à payer',
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          {formatMontant(cell)} FCFA
        </div>
      ),
    },
    {
      dataField: 'statut',
      text: 'Statut',
      formatter: (cell, row) => (
        <div onClick={() => handleRowSelect(row)}>
          <StatutPill value={cell} />
        </div>
      ),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      formatter: (cell, row) => (
        <div className="d-flex gap-1 flex-wrap" style={{ minWidth: 0 }}>
          {row.statut === 'Brouillon' && (
            <button className="fact-action-btn fact-action-outline"
              onClick={() => {
                setEditId(row.id);
                setMode('edit');
              }}>
              Modifier
            </button>
          )}
          {row.type === 'facture' && !row.entree && row.statut !== 'Annulé' && (
            <button className="fact-action-btn fact-action-green" onClick={() => onEmettre(row)}>
              Émettre
            </button>
          )}
          {row.type === 'facture' && ['En attente', 'En retard'].includes(row.statut) && (
            <button className="fact-action-btn fact-action-green" onClick={() => setPayTarget(row)}>
              Encaisser
            </button>
          )}
          {row.type === 'facture' && ['En attente', 'En retard'].includes(row.statut) && (
            <button className="fact-action-btn fact-action-outline" onClick={() => onRelancer(row)}
              title={`${row.nb_relances || 0} relance(s) envoyée(s)`}>
              Relancer
            </button>
          )}
          {row.type === 'facture' && ['En attente', 'En retard'].includes(row.statut) && (
            <button className="fact-action-btn fact-action-red" onClick={() => onAvoir(row)}>
              Avoir
            </button>
          )}
          {row.type === 'devis' && !['Accepté', 'Annulé'].includes(row.statut) && (
            <button className="fact-action-btn fact-action-outline" onClick={() => onConvertir(row)}>
              → Facture
            </button>
          )}
          {row.type !== 'avoir' && !['Annulé', 'Payé', 'En attente', 'En retard'].includes(row.statut) && (
            <button className="fact-action-btn fact-action-red" onClick={() => onAnnuler(row)}>
              Annuler
            </button>
          )}
        </div>
      ),
    },
  ];

  const typeTabs = [
    { key: '', label: 'Tous' },
    { key: 'facture', label: 'Factures' },
    ...(AFFICHER_DEVIS ? [{ key: 'devis', label: 'Devis' }] : []),
    { key: 'avoir', label: 'Avoirs' },
  ];
  const tabCount = (key) =>
    key === '' ? visibles.length : visibles.filter((f) => f.type === key).length;

  // Les KPI cards sont cliquables : elles filtrent la liste (comme dans Transactions)
  const applyKpiFilter = (type, statut) => {
    setFiltreType(type);
    setFiltreStatut(statut);
    setPaginationIndex(0);
  };

  const kpiCards = [
    {
      label: 'Total facturé TTC',
      value: `${formatMontant(kpis.totalFacture)} FCFA`,
      sub: `${visibles.filter((f) => f.type === 'facture').length} facture(s)`,
      onClick: () => applyKpiFilter('facture', ''),
      active: filtreType === 'facture' && !filtreStatut,
    },
    {
      label: 'Encaissements réalisés',
      value: `${formatMontant(kpis.encaisse)} FCFA`,
      sub: `${kpis.recouvrement} % du CA recouvré`,
      accent: '#065f46',
      onClick: () => applyKpiFilter('facture', 'Payé'),
      active: filtreStatut === 'Payé',
    },
    {
      label: 'Créances en attente',
      value: `${formatMontant(kpis.enAttente)} FCFA`,
      sub: 'Factures impayées ou partielles',
      accent: '#92400e',
      onClick: () => applyKpiFilter('facture', 'Impayées'),
      active: filtreStatut === 'Impayées',
    },
    ...(AFFICHER_DEVIS
      ? [
          {
            label: 'Devis en cours',
            value: String(kpis.nbDevis),
            sub: `${formatMontant(kpis.montantDevis)} FCFA · ${kpis.tauxConversion} % convertis`,
            accent: '#1e40af',
            onClick: () => applyKpiFilter('devis', ''),
            active: filtreType === 'devis' && !filtreStatut,
          },
        ]
      : []),
  ];

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">Factures &amp; Devis</h4>
      <span className="nbrDossier">{filtrees.length}</span>
    </div>
  );
  const description = (
    <div>
      {formatMontant(kpis.encaisse)} FCFA encaissés · {formatMontant(kpis.enAttente)} FCFA en
      attente{AFFICHER_DEVIS ? ` · ${kpis.nbDevis} devis en cours` : ''}
    </div>
  );

  if (mode === 'create') {
    return (
      <FactureForm
        dossierId={dossier_id}
        type={createType}
        onDone={(factureId) => {
          if (factureId) {
            setPreviewId(factureId);
            setMode('preview');
          } else {
            setMode('list');
          }
        }}
        onCancel={() => setMode('list')}
      />
    );
  }

  if (mode === 'edit') {
    if (!editFacture) {
      return <div style={{ padding: 40 }} className="text-muted">Chargement du document…</div>;
    }
    return (
      <FactureForm
        dossierId={dossier_id}
        type={editFacture.type}
        facture={editFacture}
        onDone={(factureId) => {
          setEditId(null);
          if (factureId) {
            setPreviewId(factureId);
            setMode('preview');
          } else {
            setMode('list');
          }
        }}
        onCancel={() => {
          setEditId(null);
          setMode('list');
        }}
      />
    );
  }

  if (mode === 'preview' && previewId) {
    return (
      <>
        <FacturePreview
          factureId={previewId}
          dossierId={dossier_id}
          onBack={() => {
            setMode('list');
            setPreviewId(null);
          }}
          onPayer={(f) => setPayTarget(f)}
        />
        <PayFactureModal
          facture={payTarget}
          show={!!payTarget}
          onHide={() => setPayTarget(null)}
        />
      </>
    );
  }

  return (
    <div
      className="global"
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '15px',
      }}
    >
      <div
        className="header pb-3"
        style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <HeaderFirst nom={nom} description={description} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {AFFICHER_DEVIS && (
            <button
              type="button"
              className="btn btn-outline-dark"
              style={{ borderRadius: '31px', border: '1px solid #AEAEAE', padding: '8px' }}
              onClick={() => {
                setCreateType('devis');
                setMode('create');
              }}
            >
              <span style={ajoutFou}>+ Nouveau devis</span>
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-primary"
            style={{
              borderRadius: '31px',
              border: '1px solid #009688',
              padding: '8px 16px',
              backgroundColor: '#009688',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
            }}
            onClick={() => {
              setCreateType('facture');
              setMode('create');
            }}
          >
            <span>+ Nouvelle facture</span>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="fact-kpi-grid">
        {kpiCards.map((k) => (
          <div
            key={k.label}
            className={`fact-kpi-card${k.active ? ' active' : ''}`}
            onClick={k.onClick}
            role="button"
            title="Cliquer pour filtrer la liste"
          >
            <div className="fact-kpi-label">{k.label}</div>
            <div className="fact-kpi-value" style={k.accent ? { color: k.accent } : undefined}>
              {k.value}
            </div>
            <div className="fact-kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      <HeaderButton
        isButtonsEnabled={selectedRows.length > 0}
        placeholder={'Rechercher par n°, client, NINEA ou montant'}
        onSearchChange={handleSearchChange}
        onEyesClick={handleEyesClick}
        onClickDelete={onClickDelete}
      />

      {/* Onglets type + filtre statut */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 mt-2">
        <div className="fact-tabs">
          {typeTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`fact-tab${filtreType === t.key ? ' active' : ''}`}
              onClick={() => {
                setFiltreType(t.key);
                setPaginationIndex(0);
              }}
            >
              {t.label} <span className="fact-tab-count">{tabCount(t.key)}</span>
            </button>
          ))}
        </div>
        <Form.Select
          size="sm"
          style={{ width: 150, borderRadius: '20px', fontSize: '13px' }}
          value={filtreStatut}
          onChange={(e) => {
            setFiltreStatut(e.target.value);
            setPaginationIndex(0);
          }}
        >
          <option value="">Tous statuts</option>
          <option>Brouillon</option>
          <option>Envoyé</option>
          <option>Accepté</option>
          <option>Émis</option>
          <option>En attente</option>
          <option>Payé</option>
          <option>En retard</option>
          <option>Impayées</option>
          <option>Annulé</option>
        </Form.Select>
      </div>

      <div>
        {isFetching && (!data || currentPageData.length === 0) ? (
          <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', border: '1px solid #e2e8f0' }}>
            <TableLoader
              message="Chargement de la facturation..."
              subMessage="Récupération des factures, devis et avoirs"
              minHeight="260px"
            />
          </div>
        ) : (
          <CustomBootstrapTable
            keyField="id"
            data={currentPageData}
            columns={columns}
            selectedRows={selectedRows}
            onSelectRow={handleRowSelect}
          />
        )}
      </div>

      {currentPageData.length > 0 && (
        <TablePaginators
          paginationIndex={paginationIndex}
          setPaginationIndex={setPaginationIndex}
          dataLength={filtrees.length}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}

      <PayFactureModal
        facture={payTarget}
        show={!!payTarget}
        onHide={() => setPayTarget(null)}
      />
      <ModalGenererAvoir
        show={!!avoirTarget}
        onHide={() => setAvoirTarget(null)}
        facture={avoirTarget}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer les brouillons"
        message={`Supprimer définitivement ${selectedRows.length} brouillon(s) ?`}
        isLoading={isDeleting}
      />
    </div>
  );
}

const ajoutFou = {
  fontFamily: 'Montserrat',
  fontWeight: '600',
  fontSize: '13px',
};
